/**
 * Cloudflare Stream Configuration
 *
 * Environment-specific configuration for Cloudflare Stream integration.
 * Customize these values based on your Cloudflare account settings.
 */

import { getAdaptiveHLSConfig } from '../utils/network-speed.js';

export const cloudflareStreamConfig = {
  // Cloudflare Account ID (found in Cloudflare Dashboard)
  accountId: import.meta.env.VITE_CLOUDFLARE_ACCOUNT_ID || null,

  // Customer subdomain (e.g., 'customer-xyz' for customer-xyz.cloudflarestream.com)
  // If not set, uses default cloudflarestream.com domain
  customerSubdomain: import.meta.env.VITE_CLOUDFLARE_CUSTOMER_SUBDOMAIN || null,

  // HLS.js configuration
  hlsConfig: {
    // Enable Web Worker for better performance
    enableWorker: true,

    // Disable low latency mode for better compatibility
    lowLatencyMode: false,

    // Buffer settings (in seconds)
    backBufferLength: 90, // How much buffer to keep behind current position
    maxBufferLength: 30, // Target forward buffer length
    maxMaxBufferLength: 60, // Maximum forward buffer length

    // Adaptive bitrate algorithm settings
    abrEwmaDefaultEstimate: 500000, // Default bandwidth estimate (500 kbps)
    abrBandWidthFactor: 0.95, // Safety factor for ABR (95% of estimated bandwidth)
    abrBandWidthUpFactor: 0.7, // Factor for upgrading quality

    // Error recovery
    manifestLoadingTimeOut: 10000, // Manifest loading timeout (ms)
    manifestLoadingMaxRetry: 4, // Max retries for manifest loading
    levelLoadingTimeOut: 10000, // Level loading timeout (ms)
    levelLoadingMaxRetry: 4, // Max retries for level loading
    fragLoadingTimeOut: 20000, // Fragment loading timeout (ms)
    fragLoadingMaxRetry: 6, // Max retries for fragment loading
  },

  // Feature flags
  features: {
    // Enable HLS streaming (set to false to use direct MP4 only)
    enableHLS: true,

    // Enable automatic quality switching based on network conditions
    enableAdaptiveBitrate: true,

    // Enable quality level logging for debugging
    enableQualityLogging: true,

    // Prefer HLS over direct MP4 when both are available
    preferHLS: true,
  },
};

/**
 * Get Cloudflare Stream configuration with adaptive HLS settings
 * @param {boolean} useAdaptive - Use adaptive HLS settings based on network speed
 * @returns {Object} Configuration object
 */
export function getCloudflareStreamConfig(useAdaptive = true) {
  if (!useAdaptive) {
    return cloudflareStreamConfig;
  }

  // Get adaptive HLS config based on current network speed
  const adaptiveHLS = getAdaptiveHLSConfig();

  // Merge adaptive settings with base config
  return {
    ...cloudflareStreamConfig,
    hlsConfig: {
      ...cloudflareStreamConfig.hlsConfig,
      ...adaptiveHLS,
    },
  };
}

/**
 * Check if Cloudflare Stream is properly configured
 * @returns {boolean} True if configured
 */
export function isCloudflareStreamConfigured() {
  return !!(cloudflareStreamConfig.accountId || cloudflareStreamConfig.customerSubdomain);
}

/**
 * Validate Cloudflare Stream configuration
 * @throws {Error} If configuration is invalid
 */
export function validateCloudflareStreamConfig() {
  if (!cloudflareStreamConfig.features.enableHLS) {
    console.warn('⚠️ HLS streaming is disabled in configuration');
    return;
  }

  if (!isCloudflareStreamConfigured()) {
    console.warn('⚠️ Cloudflare Stream not configured - will use direct MP4 delivery');
    return;
  }

  console.log('✅ Cloudflare Stream configuration validated');
  console.log(`📺 Account ID: ${cloudflareStreamConfig.accountId || 'Not set'}`);
  console.log(`🌐 Subdomain: ${cloudflareStreamConfig.customerSubdomain || 'Default'}`);
}
